import { useEffect, useRef, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { OnboardingLayout } from './components/OnboardingLayout';
import { useOnboarding } from './store/onboarding-store';
import { useOnboardingTracker } from './hooks/useOnboardingTracker';

const SuccessPage = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const { hasDomain, websiteCreated } = useOnboarding();
  const { trackCompleted } = useOnboardingTracker();
  const noDomainRef = useRef(!hasDomain);
  const showDomainMessage = noDomainRef.current;

  // The journey is not cleared here: `websiteCreated` is what tells the outlet this page was
  // earned rather than linked to directly, so clearing it would redirect the user off the page
  // they just reached. The outlet clears a finished journey when the next one starts.
  useEffect(() => {
    if (!websiteCreated) return;
    trackCompleted();
  }, [websiteCreated, trackCompleted]);

  return (
    <OnboardingLayout>
      <div className="vtwo:flex vtwo:flex-col vtwo:items-center vtwo:justify-center vtwo:text-center vtwo:gap-6 vtwo:py-20">
        <CheckCircle className="vtwo:size-16 vtwo:text-featured" />

        <h1 className="vtwo:text-3xl vtwo:font-bold">You're all set - we'll take it from here</h1>

        <div className="vtwo:flex vtwo:flex-col vtwo:gap-2 vtwo:max-w-lg">
          <p className="vtwo:text-base vtwo:text-gray-600">
            You have the basics set up. Now you may continue to build your Home Page and other
            pages.
          </p>
          {showDomainMessage && (
            <p className="vtwo:text-base vtwo:text-gray-600">
              We will contact you to set up your domain
            </p>
          )}
        </div>

        <Button
          asChild
          size="xl"
          className="vtwo:bg-featured vtwo:min-w-[240px] vtwo:hover:bg-featured-hover vtwo:rounded-full vtwo:px-16 vtwo:no-underline"
        >
          <Link to={{ pathname: '/plus-websites', search: searchParams.toString() }}>Done</Link>
        </Button>
      </div>
    </OnboardingLayout>
  );
};

export default SuccessPage;
